import type { CoordinationLinkedFeedback, CoordinationWorkItem } from '@/lib/schemas';

type ShipFields = Pick<CoordinationWorkItem, 'human_resolution' | 'stage' | 'system_verification'>;
type LinkedRow = Pick<CoordinationLinkedFeedback, 'still_broken_after_ship'>;

function readsShipped(w: ShipFields): boolean {
  return w.stage === 'on_prod' && w.system_verification === 'system_verified';
}

/**
 * v0.17.16: true when the item was shipped once and a reporter says it is
 * still broken, so "Mark as fixed" applies again after a new fix: the item is
 * latched 'regressed', or it still reads as shipped but a linked report says
 * still broken since the newest ship move.
 */
export function isRemark(w: ShipFields, linked: LinkedRow[] = []): boolean {
  if (w.human_resolution === 'regressed') return true;
  return (
    w.human_resolution === 'pending' &&
    readsShipped(w) &&
    linked.some((f) => f.still_broken_after_ship === true)
  );
}

/**
 * Whether the super-admin "Mark as fixed" control applies. Mirrors the
 * backend's services/coordination.py::plan_mark_shipped refusal rules:
 * v0.17.15, an open item (human_resolution 'pending') that does not already
 * read as shipped; v0.17.16, also a re-mark (see isRemark).
 */
export function canMarkShipped(w: ShipFields, linked: LinkedRow[] = []): boolean {
  if (isRemark(w, linked)) return true;
  if (w.human_resolution !== 'pending') return false;
  return !readsShipped(w);
}
